import React, { useState, useEffect } from "react";
import { Collapse, Modal, Button } from "react-bootstrap";
import styled from "styled-components";
import DropDownArrow from "../../assets/svg/dropdownArrow.svg"; 
import { StyledDiv, Row, Text, Img } from "./styled";
import { resetUserPassword } from "../../api/userService";

interface IUser {
  id: string;
  email: string;
  full_name?: string;
  has_pointcloud_access?: boolean;
  has_image_access?: boolean;
}

interface IGroup {
  id: number;
  name: string;
}

interface GroupProps {
  group: IGroup;
  initialOpen: boolean;
  showUsers?: boolean;
  users: IUser[];
  isSuperuser: boolean;
}

const DeleteButton = styled.button`
  background-color: #ff4d4f;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  margin-left: 10px;

  &:hover {
    background-color: #e6393c;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #7a2e30;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ResetPasswordButton = styled.button`
  background-color: #1890ff;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  margin-left: 10px;

  &:hover {
    background-color: #096dd9;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #5a7a99;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const EditButton = styled.button`
  background-color: #52c41a;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  margin-left: 10px;

  &:hover {
    background-color: #389e0d;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #5a7a5a;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const Group = ({ group, initialOpen, showUsers = false, users, isSuperuser }: GroupProps) => {
  const [open, setOpen] = useState(initialOpen);
  const [admins, setAdmins] = useState<IUser[]>([]);
  const [superusers, setSuperusers] = useState<IUser[]>([]);
  const [regularUsers, setRegularUsers] = useState<IUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    id: string;
    email: string;
    full_name?: string;
    type: "admin" | "superuser" | "regular";
  } | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState<string | null>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<{
    id: string;
    email: string;
    full_name?: string;
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<IUser | null>(null);
  const [editHasPointcloud, setEditHasPointcloud] = useState(true);
  const [editHasImage, setEditHasImage] = useState(true);
  const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const API_URL: string | undefined = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (showUsers && group.id) {
      const fetchData = async () => {
        try {
          const adminsRes = await fetch(`${API_URL}/profile/groups/${group.id}/admins`, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (adminsRes.ok) {
            const adminsData = await adminsRes.json();
            setAdmins(adminsData);
          } else if (adminsRes.status === 401) {
            setError("Not authenticated. Please log in.");
          } else if (adminsRes.status === 403) {
            setError("You do not have permission to view this group.");
          } else {
            setError(`Failed to fetch admins: ${adminsRes.statusText}`);
          }

          const regularUsersRes = await fetch(`${API_URL}/profile/groups/${group.id}/regular_users`, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (regularUsersRes.ok) {
            const regularUsersData = await regularUsersRes.json();
            setRegularUsers(regularUsersData);
          } else if (regularUsersRes.status === 401) {
            setError((prev) =>
              prev ? `${prev} | Not authenticated. Please log in.` : "Not authenticated. Please log in."
            );
          } else if (regularUsersRes.status === 403) {
            setError((prev) =>
              prev
                ? `${prev} | You do not have permission to view this group.`
                : "You do not have permission to view this group."
            );
          } else {
            setError((prev) =>
              prev ? `${prev} | Failed to fetch regular users: ${regularUsersRes.statusText}` : `Failed to fetch regular users: ${regularUsersRes.statusText}`
            );
          }

          if (isSuperuser) {
            const superusersRes = await fetch(`${API_URL}/profile/groups/${group.id}/superusers`, {
              method: "GET",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
            });
            if (superusersRes.ok) {
              const superusersData = await superusersRes.json();
              setSuperusers(superusersData);
            } else {
              setError((prev) =>
                prev ? `${prev} | Failed to fetch superusers: ${superusersRes.statusText}` : `Failed to fetch superusers: ${superusersRes.statusText}`
              );
            }
          }
        } catch (error) {
          setError(`Error fetching data: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
      };
      fetchData();
    } else if (!group.id) {
      setError("Group is missing a valid ID.");
    }
  }, [showUsers, group.id, API_URL, isSuperuser]);

  const deleteUser = async (userId: string, email: string, type: "admin" | "superuser" | "regular") => {
    if (!group.id) {
      setError("Group ID is missing.");
      return;
    }
    if (!userId && !email) {
      setError("Both user ID and email are missing.");
      return;
    }

    setIsDeleting(`${type}-${userId || email}`);
    try {
      if (userId) {
        const response = await fetch(`${API_URL}/profile/users/${userId}?group_id=${group.id}`, {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          if (type === "admin") {
            setAdmins(admins.filter((admin) => admin.id !== userId));
          } else if (type === "superuser") {
            setSuperusers(superusers.filter((superuser) => superuser.id !== userId));
          } else {
            setRegularUsers(regularUsers.filter((user) => user.id !== userId));
          }
          return;
        } else if (response.status !== 404 || !email) {
          const errorText = await response.text();
          throw new Error(`Failed to delete user by ID: ${errorText || response.statusText}`);
        }
      }

      if (email) {
        const response = await fetch(`${API_URL}/profile/users/0?group_id=${group.id}&email=${encodeURIComponent(email)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          if (type === "admin") {
            setAdmins(admins.filter((admin) => admin.email !== email));
          } else if (type === "superuser") {
            setSuperusers(superusers.filter((superuser) => superuser.email !== email));
          } else {
            setRegularUsers(regularUsers.filter((user) => user.email !== email));
          }
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to delete user by email: ${errorText || response.statusText}`);
        }
      }
    } catch (error) {
      setError(`Error deleting user account: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteClick = (user: IUser, type: "admin" | "superuser" | "regular") => {
    setUserToDelete({
      id: user.id,
      email: user.email,
      full_name: user.full_name,
      type,
    });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser(userToDelete.id, userToDelete.email, userToDelete.type);
    }
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleResetPasswordClick = (user: IUser) => {
    setUserToResetPassword({
      id: user.id,
      email: user.email,
      full_name: user.full_name,
    });
    setShowResetPasswordModal(true);
    setResetPasswordMessage(null);
  };

  const handleEditClick = (user: IUser) => {
    setUserToEdit(user);
    setEditHasPointcloud(user.has_pointcloud_access ?? true);
    setEditHasImage(user.has_image_access ?? true);
    setShowEditModal(true);
  };

  const handleConfirmEditAccess = async () => {
    if (!userToEdit) return;
    
    if (!editHasPointcloud && !editHasImage) {
      alert("User must have at least one type of access!");
      return;
    }

    setIsUpdatingAccess(true);
    try {
      const response = await fetch(`${API_URL}/profile/users/${userToEdit.id}?group_id=${group.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          has_pointcloud_access: editHasPointcloud,
          has_image_access: editHasImage,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to update user access");
      }

      // Refresh lists
      const refreshPromises = [
        fetch(`${API_URL}/profile/groups/${group.id}/admins`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }).then(res => res.ok ? res.json() : []).then(data => setAdmins(data)),
        
        fetch(`${API_URL}/profile/groups/${group.id}/regular_users`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }).then(res => res.ok ? res.json() : []).then(data => setRegularUsers(data)),
      ];

      if (isSuperuser) {
        refreshPromises.push(
          fetch(`${API_URL}/profile/groups/${group.id}/superusers`, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          }).then(res => res.ok ? res.json() : []).then(data => setSuperusers(data))
        );
      }

      await Promise.all(refreshPromises);
      setShowEditModal(false);
      setUserToEdit(null);
    } catch (error) {
      console.error("Error updating access:", error);
      alert(`Failed to update access: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsUpdatingAccess(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    if (!userToResetPassword?.id) {
      setResetPasswordMessage({
        type: "error",
        text: "User ID is missing",
      });
      return;
    }

    setIsResettingPassword(userToResetPassword.id);
    try {
      const response = await resetUserPassword(userToResetPassword.id);
      setResetPasswordMessage({
        type: "success",
        text: `Password reset successfully for ${userToResetPassword.full_name || userToResetPassword.email}.\n\nNew password: ${response.new_password}\n\nPlease save this password and share it with the user securely.`,
      });
      // Auto-close the success message after 10 seconds (longer since user needs to copy password)
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setUserToResetPassword(null);
        setResetPasswordMessage(null);
      }, 10000);
    } catch (error) {
      setResetPasswordMessage({
        type: "error",
        text: `Failed to reset password: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setIsResettingPassword(null);
    }
  };

  const handleCancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setUserToResetPassword(null);
    setResetPasswordMessage(null);
  };

  return (
    <>
      <Row
        style={{
          margin: "10px 0 0 0",
          backgroundColor: "rgba(39, 42, 55, 1)",
          borderRadius: "20px",
          padding: "10px 0",
        }}
        onClick={() => setOpen(!open)}
        aria-controls="example-collapse-text"
        aria-expanded={open}
      >
        <StyledDiv style={{ display: "flex", height: "50px" }}>
          <Text
            style={{
              fontWeight: 800,
              fontSize: "18px",
              lineHeight: "50px",
              textAlign: "right",
              marginLeft: "15px",
            }}
          >
            {group.name}
          </Text>
          <Img style={{ margin: "auto", marginRight: 0 }} height="30px" src={DropDownArrow} />
        </StyledDiv>
        <Collapse in={open}>
          <div>
            {showUsers && (
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "rgba(39, 42, 55, 0.8)",
                  borderRadius: "10px",
                  margin: "10px 0",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#ffffff",
                    marginBottom: "10px",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    paddingBottom: "5px",
                  }}
                >
                  Admins in group:
                </h3>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  {error && admins.length === 0 ? (
                    <div style={{ color: "#ff4d4f", fontSize: "14px", padding: "5px 0" }}>{error}</div>
                  ) : admins.length > 0 ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {admins.map((admin, index) => (
                        <li
                          key={`admin-${admin.id || admin.email}-${index}`}
                          style={{
                            fontSize: "14px",
                            color: "#e0e0e0",
                            padding: "5px 0",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span>
                              {admin.full_name || "Name missing"} (
                              <span style={{ color: "#b0b0b0" }}>{admin.email || "Email missing"}</span>)
                            </span>
                            <span style={{ display: "flex", gap: "4px", fontSize: "16px" }}>
                              {admin.has_pointcloud_access !== false && <span title="Punktmoln access">🗺️</span>}
                              {admin.has_image_access !== false && <span title="Bild access">📷</span>}
                            </span>
                          </span>
                          {isSuperuser && (
                            <ButtonGroup>
                              <EditButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(admin);
                                }}
                              >
                                Edit Access
                              </EditButton>
                              <ResetPasswordButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetPasswordClick(admin);
                                }}
                                disabled={isResettingPassword === admin.id || !admin.id}
                              >
                                {isResettingPassword === admin.id ? "Resetting..." : "Reset Password"}
                              </ResetPasswordButton>
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(admin, "admin");
                                }}
                                disabled={isDeleting === `admin-${admin.id || admin.email}` || (!admin.id && !admin.email)}
                              >
                                {isDeleting === `admin-${admin.id || admin.email}` ? "Deleting..." : "Delete Account"}
                              </DeleteButton>
                            </ButtonGroup>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ fontSize: "14px", color: "#b0b0b0", padding: "5px 0" }}>
                      No admins to display.
                    </div>
                  )}
                </div>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#ffffff",
                    marginTop: "20px",
                    marginBottom: "10px",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    paddingBottom: "5px",
                  }}
                >
                  Users in group:
                </h3>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  {error && regularUsers.length === 0 ? (
                    <div style={{ color: "#ff4d4f", fontSize: "14px", padding: "5px 0" }}>{error}</div>
                  ) : regularUsers.length > 0 ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {regularUsers.map((user, index) => (
                        <li
                          key={`regular-${user.id || user.email}-${index}`}
                          style={{
                            fontSize: "14px",
                            color: "#e0e0e0",
                            padding: "5px 0",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span>
                              {user.full_name || "Name missing"} (
                              <span style={{ color: "#b0b0b0" }}>{user.email || "Email missing"}</span>)
                            </span>
                            <span style={{ display: "flex", gap: "4px", fontSize: "16px" }}>
                              {user.has_pointcloud_access !== false && <span title="Punktmoln access">🗺️</span>}
                              {user.has_image_access !== false && <span title="Bild access">📷</span>}
                            </span>
                          </span>
                          {isSuperuser && (
                            <ButtonGroup>
                              <EditButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(user);
                                }}
                              >
                                Edit Access
                              </EditButton>
                              <ResetPasswordButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetPasswordClick(user);
                                }}
                                disabled={isResettingPassword === user.id || !user.id}
                              >
                                {isResettingPassword === user.id ? "Resetting..." : "Reset Password"}
                              </ResetPasswordButton>
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(user, "regular");
                                }}
                                disabled={isDeleting === `regular-${user.id || user.email}` || (!user.id && !user.email)}
                              >
                                {isDeleting === `regular-${user.id || user.email}` ? "Deleting..." : "Delete Account"}
                              </DeleteButton>
                            </ButtonGroup>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ fontSize: "14px", color: "#b0b0b0", padding: "5px 0" }}>
                      No users to display.
                    </div>
                  )}
                </div>

                {isSuperuser && (
                  <>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#ffffff",
                        marginTop: "20px",
                        marginBottom: "10px",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                        paddingBottom: "5px",
                      }}
                    >
                      Superusers in group:
                    </h3>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        padding: "10px",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "8px",
                      }}
                    >
                      {error && superusers.length === 0 ? (
                        <div style={{ color: "#ff4d4f", fontSize: "14px", padding: "5px 0" }}>{error}</div>
                      ) : superusers.length > 0 ? (
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                          {superusers.map((superuser, index) => (
                            <li
                              key={`superuser-${superuser.id || superuser.email}-${index}`}
                              style={{
                                fontSize: "14px",
                                color: "#e0e0e0",
                                padding: "5px 0",
                                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span>
                                  {superuser.full_name || "Name missing"} (
                                  <span style={{ color: "#b0b0b0" }}>{superuser.email || "Email missing"}</span>)
                                </span>
                                <span style={{ display: "flex", gap: "4px", fontSize: "16px" }}>
                                  {superuser.has_pointcloud_access !== false && <span title="Punktmoln access">🗺️</span>}
                                  {superuser.has_image_access !== false && <span title="Bild access">📷</span>}
                                </span>
                              </span>
                              {isSuperuser && (
                                <ButtonGroup>
                                  <EditButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditClick(superuser);
                                    }}
                                  >
                                    Edit Access
                                  </EditButton>
                                  <ResetPasswordButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResetPasswordClick(superuser);
                                    }}
                                    disabled={isResettingPassword === superuser.id || !superuser.id}
                                  >
                                    {isResettingPassword === superuser.id ? "Resetting..." : "Reset Password"}
                                  </ResetPasswordButton>
                                  <DeleteButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(superuser, "superuser");
                                    }}
                                    disabled={isDeleting === `superuser-${superuser.id || superuser.email}` || (!superuser.id && !superuser.email)}
                                  >
                                    {isDeleting === `superuser-${superuser.id || superuser.email}` ? "Deleting..." : "Delete Account"}
                                  </DeleteButton>
                                </ButtonGroup>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ fontSize: "14px", color: "#b0b0b0", padding: "5px 0" }}>
                          No superusers to display.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Collapse>
      </Row>

      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {" "}
          <strong>{userToDelete?.full_name || userToDelete?.email || "this user"}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting !== null}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showResetPasswordModal} onHide={handleCancelResetPassword} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetPasswordMessage ? (
            <div
              style={{
                padding: "10px",
                borderRadius: "5px",
                backgroundColor: resetPasswordMessage.type === "success" ? "#d4edda" : "#f8d7da",
                color: resetPasswordMessage.type === "success" ? "#155724" : "#721c24",
                border: `1px solid ${resetPasswordMessage.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
              }}
            >
              {resetPasswordMessage.text}
            </div>
          ) : (
            <>
              Are you sure you want to reset the password for {" "}
              <strong>{userToResetPassword?.full_name || userToResetPassword?.email || "this user"}</strong>?
              <br />
              <br />
              A new password will be generated and sent to their email address.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelResetPassword}>
            {resetPasswordMessage?.type === "success" ? "Close" : "Cancel"}
          </Button>
          {!resetPasswordMessage && (
            <Button
              variant="primary"
              onClick={handleConfirmResetPassword}
              disabled={isResettingPassword !== null}
            >
              {isResettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "15px" }}>
            <strong>{userToEdit?.full_name || userToEdit?.email}</strong>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={editHasPointcloud}
                onChange={(e) => setEditHasPointcloud(e.target.checked)}
                style={{ width: "18px", height: "18px" }}
              />
              <span>🗺️ Punktmoln (LIDAR) Access</span>
            </label>
          </div>
          <div>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={editHasImage}
                onChange={(e) => setEditHasImage(e.target.checked)}
                style={{ width: "18px", height: "18px" }}
              />
              <span>📷 Bild/Panorama Access</span>
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmEditAccess}
            disabled={isUpdatingAccess || (!editHasPointcloud && !editHasImage)}
          >
            {isUpdatingAccess ? "Updating..." : "Update Access"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Group;